import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { clearDiagnostics } from '../feedback/diagnostics';
import { reportBrowserFailure } from '../telemetry';
import { isApiError, isClientError, isNotSignedIn } from './errors';

export interface QueryClientOptions {
  /**
   * Called when any query or mutation answers 401 after the app has loaded (the session ended
   * while the person was working). Route loaders handle the first load themselves, and a
   * query or mutation with `meta: { handlesNotSignedIn: true }` handles its own.
   */
  onNotSignedIn?: () => void;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: { handlesNotSignedIn?: boolean; busy?: boolean };
    mutationMeta: { handlesNotSignedIn?: boolean; busy?: boolean };
  }
}

/** One QueryClient for the app, shared by components and route loaders. */
export function createQueryClient({ onNotSignedIn }: QueryClientOptions = {}): QueryClient {
  const handle = (error: unknown, meta: { handlesNotSignedIn?: boolean } | undefined): void => {
    reportBrowserFailure(error, 'api_error');
    if (isNotSignedIn(error) && meta?.handlesNotSignedIn !== true) {
      // The session ended: nothing of it stays in this tab's feedback rings.
      clearDiagnostics();
      onNotSignedIn?.();
    }
  };
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        handle(error, query.meta);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _result, mutation) => {
        handle(error, mutation.meta);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        // A 4xx won't change on retry; a network blip or a 5xx might.
        retry: (failureCount, error) =>
          !isClientError(error) &&
          !(isApiError(error) && error.code === 'invalid_response') &&
          failureCount < 2,
      },
      mutations: { retry: false },
    },
  });
}
